import uuidv4 from 'uuid/v4';
import basic from './common/basic.js';
import rect from './common/rect.js';
import updater from '../updater.js';
import { unwrapMention, setMentionContent } from '../mention-helper.js';

/* State example:
state: {
  byId: {
    "cond_1": {
      "script": null,
      "output": null,
    },
    "cond_2": {
      "script": null,
      "output": null,
    },
  }
}
*/
const NEW_CONDITION = { script: null, output: null };
const options = {};

const state = { byId: {} };

const getters = {
  ...basic.getters(options),
  ...rect.getters(options),
};

const actions = {
  ...basic.actions({ type: 'conditions', container: null }),

  /**
   * Create a new default condition
   * @param {Object}  props     Customization to the default values
   * @param {Boolean} apply     Whether to apply the patch after finishing
   */
  customCreate(context, { props = {}, apply }) {
    const id = uuidv4();
    const condition = { id, ...NEW_CONDITION, ...props };
    context.dispatch('create', {
      node: condition,
      tId: null,
      tIndex: null,
      apply: false,
    });
    updater.complete({ apply });
    return Promise.resolve(id);
  },

  /**
   * Update some of the condition's properties
   * @param {String}  id    The condition id
   * @param {Object}  props Contains only the properties that are changed
   * @param {Boolean} apply Whether to apply the patch after finishing
   */
  customUpdate(context, { id, props, apply }) {
    context.dispatch('update', { id, props, apply: false });
    updater.complete({ apply });
  },

  /**
   * Remove a condition with specified id from the store
   * @param {String}  id      The condition id
   * @param {Boolean} apply   Whether to apply the patch after finishing
   */
  customDelete(context, { id, apply }) {
    // Delete the condition object
    context.dispatch('delete', { id, apply: false });
    // Delete its connection
    context.dispatch(
      'project/data/connections/deleteConnections',
      { id, apply: false },
      { root: true }
    );
    updater.complete({ apply });
  },

  /**
   * Remove mentions from all conditions.
   * @param {array}   ids       The ids of the mentioned objects
   * @param {String}  type      The type of the mentioned objects
   */
  deleteMentions(context, { ids, type, apply }) {
    const patch = [];
    Object.values(context.state.byId).forEach(condition => {
      const text = unwrapMention(condition.script, ids, type);
      if (text !== condition.script) {
        patch.push({
          op: 'replace',
          path: `/data/conditions/byId/${condition.id}/script`,
          value: text,
        });
      }
    });
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Update the name in mentions in all conditions.
   * @param {String} id     The id of the mentioned object
   * @param {String} type   The type of the mentioned object
   * @param {String} name   The new name of the mentioned object
   * @param {String} label  The original label of the mention if different from name
   */
  updateMentions(context, { id, type, name, label, apply }) {
    const patch = [];
    Object.values(context.state.byId).forEach(condition => {
      const text = setMentionContent(condition.script, id, type, name, label);
      if (text !== condition.script) {
        patch.push({
          op: 'replace',
          path: `/data/conditions/byId/${condition.id}/script`,
          value: text,
        });
      }
    });
    updater.add(patch);
    updater.complete({ apply });
  },
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations: {},
  actions,
};

export default module;
